import {
  DocumentCategory,
  DocumentRecord,
  DocumentVersionRecord,
  DocumentReview,
  DocumentApproval,
  DocumentAcknowledgement,
  DocumentChangeRequest,
  DocumentCrossLink,
  DocumentStatusHistory,
  DocumentsDashboardKpis,
} from './types';

const BASE_URL = '/api/documents';
const DEFAULT_ORG_ID = 'ORG-XYZ';

function getHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'x-organization-id': DEFAULT_ORG_ID,
  };
}

export const documentsService = {
  // KPI Analytics
  async getDashboardKpis(): Promise<DocumentsDashboardKpis> {
    const res = await fetch(`${BASE_URL}/analytics/dashboard`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  // Categories
  async getCategories(): Promise<DocumentCategory[]> {
    const res = await fetch(`${BASE_URL}/categories`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  // Document Master Register
  async getDocuments(filters?: {
    category_id?: string;
    status?: string;
    department?: string;
    search?: string;
  }): Promise<DocumentRecord[]> {
    const params = new URLSearchParams();
    if (filters?.category_id) params.append('category_id', filters.category_id);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.department) params.append('department', filters.department);
    if (filters?.search) params.append('search', filters.search);

    const res = await fetch(`${BASE_URL}?${params.toString()}`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  async getDocumentById(id: string): Promise<DocumentRecord & { versions?: DocumentVersionRecord[] }> {
    const res = await fetch(`${BASE_URL}/${id}`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  async createDocument(data: {
    document_number: string;
    title: string;
    category_id: string;
    department?: string;
    confidentiality?: string;
    classification?: string;
    language?: string;
    owner_id: string;
    owner_name: string;
    review_cycle_months?: number;
    requires_acknowledgement?: boolean;
    description?: string;
    initial_version_number?: string;
  }): Promise<DocumentRecord> {
    const res = await fetch(BASE_URL, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to create document');
    return json.data;
  },

  // Versions
  async getVersions(documentId: string): Promise<DocumentVersionRecord[]> {
    const res = await fetch(`${BASE_URL}/${documentId}/versions`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  async createNewVersion(documentId: string, data: {
    version_number: string;
    major_version: number;
    minor_version: number;
    change_summary: string;
    reason_for_revision?: string;
    file_name?: string;
    file_url?: string;
    created_by_id: string;
    created_by_name: string;
  }): Promise<DocumentVersionRecord> {
    const res = await fetch(`${BASE_URL}/${documentId}/versions`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to create new version');
    return json.data;
  },

  // Review Workflow
  async requestReview(documentId: string, payload: {
    version_id: string;
    reviewer: { id: string; name: string; role?: string };
    actor: { id: string; name: string };
  }): Promise<DocumentReview> {
    const res = await fetch(`${BASE_URL}/${documentId}/reviews`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to request review');
    return json.data;
  },

  async completeReview(reviewId: string, payload: {
    decision: { recommendation: 'APPROVED' | 'REJECTED' | 'CHANGES_REQUESTED'; comments?: string };
    actor: { id: string; name: string };
  }): Promise<DocumentReview> {
    const res = await fetch(`${BASE_URL}/reviews/${reviewId}/complete`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to complete review');
    return json.data;
  },

  // Approval Workflow
  async submitForApproval(documentId: string, payload: {
    version_id: string;
    approver: { id: string; name: string; role_title: string };
    actor: { id: string; name: string };
  }): Promise<DocumentApproval> {
    const res = await fetch(`${BASE_URL}/${documentId}/approvals`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to submit for approval');
    return json.data;
  },

  async decideApproval(approvalId: string, payload: {
    decision: { decision: 'APPROVED' | 'REJECTED'; comments?: string; digital_signature?: string };
    actor: { id: string; name: string };
  }): Promise<DocumentApproval> {
    const res = await fetch(`${BASE_URL}/approvals/${approvalId}/decide`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to decide approval');
    return json.data;
  },

  // Publication & Distribution
  async publishDocument(documentId: string, payload: {
    version_id: string;
    effective_date: string;
    actor: { id: string; name: string };
    distribution_user_ids?: string[];
  }): Promise<DocumentRecord> {
    const res = await fetch(`${BASE_URL}/${documentId}/publish`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to publish document');
    return json.data;
  },

  // Acknowledgements
  async getAcknowledgements(filters?: { document_id?: string; user_id?: string; status?: string }): Promise<DocumentAcknowledgement[]> {
    const params = new URLSearchParams();
    if (filters?.document_id) params.append('document_id', filters.document_id);
    if (filters?.user_id) params.append('user_id', filters.user_id);
    if (filters?.status) params.append('status', filters.status);

    const res = await fetch(`${BASE_URL}/acknowledgements?${params.toString()}`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  async recordAcknowledgement(acknowledgementId: string, userId: string, notes?: string): Promise<DocumentAcknowledgement> {
    const res = await fetch(`${BASE_URL}/acknowledgements/${acknowledgementId}/sign`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ user_id: userId, notes }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to record acknowledgement');
    return json.data;
  },

  // Change Requests
  async getChangeRequests(filters?: { target_document_id?: string; status?: string; source_type?: string }): Promise<DocumentChangeRequest[]> {
    const params = new URLSearchParams();
    if (filters?.target_document_id) params.append('target_document_id', filters.target_document_id);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.source_type) params.append('source_type', filters.source_type);

    const res = await fetch(`${BASE_URL}/change-requests?${params.toString()}`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  async createChangeRequest(data: {
    target_document_id: string;
    requester_id: string;
    requester_name: string;
    source_type?: string;
    priority?: string;
    title: string;
    reason: string;
    proposed_changes?: string;
  }): Promise<DocumentChangeRequest> {
    const res = await fetch(`${BASE_URL}/change-requests`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to create change request');
    return json.data;
  },

  // Cross-Links & Audit Trail
  async getCrossLinks(documentId: string): Promise<DocumentCrossLink[]> {
    const res = await fetch(`${BASE_URL}/${documentId}/cross-links`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },

  async getStatusHistory(documentId: string): Promise<DocumentStatusHistory[]> {
    const res = await fetch(`${BASE_URL}/${documentId}/history`, { headers: getHeaders() });
    const json = await res.json();
    return json.data;
  },
};

export { docDistributionService } from './docDistributionService';
